import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Skeleton,
  FormModal,
  Input,
  Select,
  TextArea,
} from '../../components/ui';
import { adminAPI } from '../../services/adminApi';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import SubNavigation from '../../components/admin/SubNavigation';
import {
  Search,
  Restaurant,
  LocalFireDepartment,
  Warning,
  Close,
  Visibility,
  Add,
  Edit,
  Delete,
  Category,
  Palette,
  Analytics,
  // Ícones para categorias de ingredientes
  WaterDrop,
  LocalCafe,
  BakeryDining,
  Grain,
  Egg,
  SetMeal,
  Icecream,
  Apple,
  Spa,
  Grass,
  EggAlt,
  RiceBowl,
  LunchDining,
  LocalPizza,
  Cookie,
  Cake,
  EmojiFoodBeverage,
  SportsBar,
  LocalBar,
  Liquor,
  Coffee,
  Blender,
  Kitchen,
  Fastfood,
  KebabDining,
  // Ícones adicionais
  Opacity,
  LocalDrink,
  EnergySavingsLeaf,
  QuestionMark,
  HelpOutline,
  Science,
  FitnessCenter,
  Yard,
  Park,
  Nature,
  FilterVintage,
  LocalFlorist,
  Inventory,
  BreakfastDining,
  DinnerDining,
  Medication
} from '../../mui-icons';
import './IngredientsPage.css';

// Mapeamento de nomes de ícones para componentes
const ICON_MAP = {
  // Bebidas
  water: WaterDrop,
  water_drop: WaterDrop,
  local_cafe: LocalCafe,
  coffee: Coffee,
  emoji_food_beverage: EmojiFoodBeverage,
  sports_bar: SportsBar,
  local_bar: LocalBar,
  liquor: Liquor,
  local_drink: LocalDrink,
  // Grãos e Carboidratos
  bread: BakeryDining,
  bakery_dining: BakeryDining,
  grain: Grain,
  rice_bowl: RiceBowl,
  breakfast_dining: BreakfastDining,
  dinner_dining: DinnerDining,
  // Proteínas
  egg: Egg,
  egg_alt: EggAlt,
  set_meal: SetMeal,
  lunch_dining: LunchDining,
  kebab_dining: KebabDining,
  protein: FitnessCenter,
  fitness_center: FitnessCenter,
  // Frutas e Vegetais
  apple: Apple,
  spa: Spa,
  grass: Grass,
  yard: Yard,
  park: Park,
  nature: Nature,
  eco: Yard,
  // Vegetais e Folhas
  leaf: EnergySavingsLeaf,
  energy_savings_leaf: EnergySavingsLeaf,
  local_florist: LocalFlorist,
  filter_vintage: FilterVintage,
  // Doces e Lanches
  icecream: Icecream,
  ice_cream: Icecream,
  candy: Cake,
  cookie: Cookie,
  cake: Cake,
  fastfood: Fastfood,
  local_pizza: LocalPizza,
  // Gorduras e Óleos
  oil: Opacity,
  opacity: Opacity,
  // Laticínios
  milk: LocalDrink,
  // Temperos
  spice: FilterVintage,
  medication: Medication,
  science: Science,
  // Outros / Mista
  question: HelpOutline,
  question_mark: QuestionMark,
  help_outline: HelpOutline,
  inventory: Inventory,
  // Geral
  restaurant: Restaurant,
  blender: Blender,
  kitchen: Kitchen,
  category: Category
};

// Componente para renderizar ícone dinamicamente
const CategoryIcon = ({ iconName, size = 14 }) => {
  const IconComponent = ICON_MAP[iconName?.toLowerCase()];
  if (!IconComponent) return null;
  return <IconComponent sx={{ fontSize: size }} />;
};

const IngredientsPage = () => {
  const { canEdit } = useAdminAuth();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('ingredients'); // 'categories' | 'ingredients'
  
  // ==================== CATEGORIES STATE ====================
  const [categoriesList, setCategoriesList] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesSearch, setCategoriesSearch] = useState('');
  const [categoryModal, setCategoryModal] = useState({ open: false, mode: 'create', data: null });
  
  // ==================== INGREDIENTS STATE ====================
  const [ingredients, setIngredients] = useState([]);
  const [ingredientsLoading, setIngredientsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [ingredientModal, setIngredientModal] = useState({ open: false, mode: 'create', data: null });
  
  // Detail Modal
  const [selectedIngredient, setSelectedIngredient] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [ingredientDetail, setIngredientDetail] = useState(null);

  // Action loading
  const [actionLoading, setActionLoading] = useState(false);

  // ==================== LOAD CATEGORIES ====================
  const loadCategories = useCallback(async () => {
    setCategoriesLoading(true);
    try {
      const params = { limit: 200 };
      if (categoriesSearch) params.search = categoriesSearch;
      
      const response = await adminAPI.getIngredientCategories(params);
      if (response.status === 'success') {
        setCategoriesList(response.data.categories || response.data || []);
      }
    } catch (err) {
      console.error('Erro ao carregar categorias:', err);
    } finally {
      setCategoriesLoading(false);
    }
  }, [categoriesSearch]);

  // ==================== LOAD INGREDIENTS ====================
  const loadIngredients = useCallback(async () => {
    setIngredientsLoading(true);
    setError(null);

    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(search && { search }),
        ...(categoryFilter && { category: categoryFilter })
      };

      const response = await adminAPI.getIngredients(params);
      
      if (response.status === 'success') {
        setIngredients(response.data.ingredients || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          pages: response.data.pagination?.pages || 0
        }));
      }
    } catch (err) {
      console.error('Erro ao carregar ingredientes:', err);
      setError(err.message);
    } finally {
      setIngredientsLoading(false);
    }
  }, [pagination.page, pagination.limit, search, categoryFilter]);

  // Load data on mount and tab change
  useEffect(() => {
    if (activeTab === 'categories') {
      loadCategories();
    } else {
      loadIngredients();
      // Also load categories for the dropdown
      if (categoriesList.length === 0) {
        loadCategories();
      }
    }
  }, [activeTab, loadCategories, loadIngredients, categoriesList.length]);

  // ==================== CATEGORY ACTIONS ====================
  const handleSaveCategory = async (formData) => {
    setActionLoading(true);
    try {
      if (categoryModal.mode === 'create') {
        await adminAPI.createIngredientCategory(formData);
      } else {
        await adminAPI.updateIngredientCategory(categoryModal.data.idingredientcategory, formData);
      }
      setCategoryModal({ open: false, mode: 'create', data: null });
      loadCategories();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!window.confirm(`Excluir a categoria "${category.category_name}"?\n\nIsso só é possível se não houver ingredientes nesta categoria.`)) {
      return;
    }
    
    setActionLoading(true);
    try {
      await adminAPI.deleteIngredientCategory(category.idingredientcategory);
      loadCategories();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // ==================== INGREDIENT ACTIONS ====================
  const handleSaveIngredient = async (formData) => {
    setActionLoading(true);
    try {
      if (ingredientModal.mode === 'create') {
        await adminAPI.createIngredient(formData);
      } else {
        await adminAPI.updateIngredient(ingredientModal.data.idingredients, formData);
      }
      setIngredientModal({ open: false, mode: 'create', data: null });
      loadIngredients();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteIngredient = async (ingredient) => {
    if (!window.confirm(`Excluir o ingrediente "${ingredient.name}"?\n\nIsso só é possível se não estiver em uso por usuários.`)) {
      return;
    }
    
    setActionLoading(true);
    try {
      await adminAPI.deleteIngredient(ingredient.idingredients);
      loadIngredients();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Open detail modal
  const openDetail = async (ingredient) => {
    setSelectedIngredient(ingredient);
    setDetailLoading(true);
    setIngredientDetail(null);

    try {
      const response = await adminAPI.getIngredient(ingredient.idingredients);
      if (response.status === 'success') {
        setIngredientDetail(response.data);
      }
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  // Close detail modal
  const closeDetail = () => {
    setSelectedIngredient(null);
    setIngredientDetail(null);
  };

  // Format number
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return num.toFixed(1);
  };

  // Filtered categories
  const filteredCategories = categoriesSearch 
    ? categoriesList.filter(c => c.category_name.toLowerCase().includes(categoriesSearch.toLowerCase()))
    : categoriesList;

  // Get category info by name (for colored badges in table)
  const getCategoryInfo = (categoryName) => {
    return categoriesList.find(c => c.category_name === categoryName);
  };

  // Sub-navigation items para o grupo Alimentos e Refeições
  const subNavItems = [
    { path: '/admin/meals', label: 'Estatísticas', icon: <Analytics sx={{ fontSize: 18 }} /> },
    { path: '/admin/ingredients', label: 'Alimentos', icon: <Restaurant sx={{ fontSize: 18 }} /> }
  ];

  return (
    <div className="ingredients-page">
      {/* Sub Navigation */}
      <SubNavigation items={subNavItems} />

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <Restaurant sx={{ fontSize: 32 }} />
          <div>
            <h1>Alimentos e Categorias</h1>
            <p>Gerenciamento do catálogo de alimentos</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <button 
          className={`tab-btn ${activeTab === 'ingredients' ? 'active' : ''}`}
          onClick={() => setActiveTab('ingredients')}
        >
          <Restaurant sx={{ fontSize: 20 }} />
          Ingredientes
        </button>
        <button 
          className={`tab-btn ${activeTab === 'categories' ? 'active' : ''}`}
          onClick={() => setActiveTab('categories')}
        >
          <Category sx={{ fontSize: 20 }} />
          Categorias
        </button>
      </div>

      {/* ==================== CATEGORIES TAB ==================== */}
      {activeTab === 'categories' && (
        <div className="tab-content">
          {/* Filters */}
          <div className="admin-card pn-card pn-card--default filters-card">
            <div className="filters-row">
              <div className="search-box">
                <Search className="search-icon" />
                <input
                  type="text"
                  placeholder="Buscar categoria..."
                  value={categoriesSearch}
                  onChange={(e) => setCategoriesSearch(e.target.value)}
                />
              </div>
              {canEdit() && (
                <Button
                  variant="primary"
                  onClick={() => setCategoryModal({ open: true, mode: 'create', data: null })}
                >
                  <Add sx={{ fontSize: 18 }} />
                  Nova Categoria
                </Button>
              )}
            </div>
          </div>

          {/* Categories List */}
          <div className="admin-card pn-card pn-card--default">
            <div className="categories-grid">
              {categoriesLoading ? (
                [...Array(6)].map((_, i) => (
                  <div key={i} className="category-card skeleton-card">
                    <Skeleton height={100} />
                  </div>
                ))
              ) : filteredCategories.length === 0 ? (
                <div className="empty-state full-width">
                  <Category sx={{ fontSize: 48, opacity: 0.3 }} />
                  <p>Nenhuma categoria encontrada</p>
                  {canEdit() && (
                    <Button
                      variant="primary"
                      onClick={() => setCategoryModal({ open: true, mode: 'create', data: null })}
                    >
                      <Add /> Criar primeira categoria
                    </Button>
                  )}
                </div>
              ) : (
                filteredCategories.map(category => (
                  <div key={category.idingredientcategory} className="category-card">
                    <div 
                      className="category-color-bar" 
                      style={{ backgroundColor: category.color_hex || '#9B59B6' }}
                    />
                    <div className="category-content">
                      <div className="category-header">
                        <h3>{category.category_name}</h3>
                        <span 
                          className="category-color-badge"
                          style={{ backgroundColor: category.color_hex || '#9B59B6' }}
                          title={category.color_hex}
                        />
                      </div>
                      {category.category_description && (
                        <p className="category-description">{category.category_description}</p>
                      )}
                      <div className="category-meta">
                        {category.icon_name && (
                          <span className="category-icon">
                            <CategoryIcon iconName={category.icon_name} size={16} />
                          </span>
                        )}
                        <span className="category-count">
                          {category.ingredients_count || 0} ingrediente(s)
                        </span>
                      </div>
                      {canEdit() && (
                        <div className="category-actions">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setCategoryModal({ open: true, mode: 'edit', data: category })}
                            title="Editar"
                            aria-label="Editar"
                          >
                            <Edit sx={{ fontSize: 16 }} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCategory(category)}
                            title="Excluir"
                            aria-label="Excluir"
                            disabled={category.ingredients_count > 0}
                          >
                            <Delete sx={{ fontSize: 16 }} />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ==================== INGREDIENTS TAB ==================== */}
      {activeTab === 'ingredients' && (
        <div className="tab-content">
          {/* Filters */}
          <div className="admin-card pn-card pn-card--default filters-card">
            <div className="filters-row">
              <div className="search-box">
                <Search className="search-icon" />
                <input
                  type="text"
                  placeholder="Buscar por nome..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                />
              </div>

              <div className="filter-group">
                <label>Categoria</label>
                <select
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                >
                  <option value="">Todas</option>
                  {categoriesList.map(cat => (
                    <option key={cat.idingredientcategory || cat.category_name} value={cat.category_name}>
                      {cat.category_name}
                    </option>
                  ))}
                </select>
              </div>

              {canEdit() && (
                <Button
                  variant="primary"
                  onClick={() => setIngredientModal({ open: true, mode: 'create', data: null })}
                >
                  <Add sx={{ fontSize: 18 }} />
                  Novo Ingrediente
                </Button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="admin-card pn-card pn-card--default">
            {error ? (
              <div className="error-state">
                <Warning sx={{ fontSize: 48 }} />
                <p>{error}</p>
                <Button variant="primary" onClick={loadIngredients}>
                  Tentar novamente
                </Button>
              </div>
            ) : (
              <>
                <div className="table-header-info">
                  <span>{pagination.total} ingrediente(s) encontrado(s)</span>
                </div>

                <div className="table-responsive">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>Categoria</th>
                        <th>Calorias</th>
                        <th>Proteína</th>
                        <th>Carboidratos</th>
                        <th>Gordura</th>
                        <th>Fibra</th>
                        <th style={{ width: '120px' }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ingredientsLoading ? (
                        [...Array(10)].map((_, i) => (
                          <tr key={i}>
                            <td><Skeleton width={150} height={20} /></td>
                            <td><Skeleton width={100} height={20} /></td>
                            <td><Skeleton width={60} height={20} /></td>
                            <td><Skeleton width={60} height={20} /></td>
                            <td><Skeleton width={60} height={20} /></td>
                            <td><Skeleton width={60} height={20} /></td>
                            <td><Skeleton width={60} height={20} /></td>
                            <td><Skeleton width={80} height={32} /></td>
                          </tr>
                        ))
                      ) : ingredients.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="empty-state">
                            <Restaurant sx={{ fontSize: 48, opacity: 0.3 }} />
                            <p>Nenhum ingrediente encontrado</p>
                          </td>
                        </tr>
                      ) : (
                        ingredients.map(ingredient => (
                          <tr key={ingredient.idingredients}>
                            <td>
                              <strong>{ingredient.name}</strong>
                            </td>
                            <td>
                              {ingredient.category ? (
                                <span 
                                  className="category-badge-colored"
                                  style={{ 
                                    backgroundColor: getCategoryInfo(ingredient.category)?.color_hex || '#9B59B6',
                                    color: 'white'
                                  }}
                                >
                                  <CategoryIcon iconName={getCategoryInfo(ingredient.category)?.icon_name} size={14} />
                                  {ingredient.category}
                                </span>
                              ) : (
                                <span className="category-badge">-</span>
                              )}
                            </td>
                            <td>
                              <span className="nutrient-value calories">
                                <LocalFireDepartment sx={{ fontSize: 14 }} />
                                {formatNumber(ingredient.calories_per_100g)}
                              </span>
                            </td>
                            <td>
                              <span className="nutrient-value protein">{formatNumber(ingredient.protein_per_100g)}g</span>
                            </td>
                            <td>
                              <span className="nutrient-value carbs">{formatNumber(ingredient.carbs_per_100g)}g</span>
                            </td>
                            <td>
                              <span className="nutrient-value fat">{formatNumber(ingredient.fat_per_100g)}g</span>
                            </td>
                            <td>
                              <span className="nutrient-value fiber">{formatNumber(ingredient.fiber_per_100g)}g</span>
                            </td>
                            <td>
                              <div className="table-actions">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openDetail(ingredient)}
                                  title="Ver detalhes"
                                  aria-label="Ver detalhes"
                                >
                                  <Visibility sx={{ fontSize: 18 }} />
                                </Button>
                                {canEdit() && (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setIngredientModal({ open: true, mode: 'edit', data: ingredient })}
                                      title="Editar"
                                      aria-label="Editar"
                                    >
                                      <Edit sx={{ fontSize: 18 }} />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteIngredient(ingredient)}
                                      title="Excluir"
                                      aria-label="Excluir"
                                    >
                                      <Delete sx={{ fontSize: 18 }} />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="pagination">
                    <button
                      className="pagination-btn"
                      disabled={pagination.page === 1}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    >
                      Anterior
                    </button>
                    <span className="pagination-info">
                      Página {pagination.page} de {pagination.pages}
                    </span>
                    <button
                      className="pagination-btn"
                      disabled={pagination.page === pagination.pages}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    >
                      Próxima
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ==================== CATEGORY MODAL ==================== */}
      {categoryModal.open && (
        <CategoryModal
          mode={categoryModal.mode}
          data={categoryModal.data}
          loading={actionLoading}
          onClose={() => setCategoryModal({ open: false, mode: 'create', data: null })}
          onSave={handleSaveCategory}
        />
      )}

      {/* ==================== INGREDIENT MODAL ==================== */}
      {ingredientModal.open && (
        <IngredientModal
          mode={ingredientModal.mode}
          data={ingredientModal.data}
          categories={categoriesList}
          loading={actionLoading}
          onClose={() => setIngredientModal({ open: false, mode: 'create', data: null })}
          onSave={handleSaveIngredient}
        />
      )}

      {/* ==================== DETAIL MODAL ==================== */}
      <FormModal
        open={Boolean(selectedIngredient)}
        onClose={closeDetail}
        title={selectedIngredient?.name || 'Detalhes'}
        large
      >
            {detailLoading ? (
              <div className="modal-loading">
                <Skeleton height={200} />
              </div>
            ) : ingredientDetail ? (
              <div className="modal-body">
                <div className="ingredient-detail">
                  <div className="detail-header">
                    <span className="category-badge large">{ingredientDetail.category || 'Sem categoria'}</span>
                  </div>

                  <div className="macros-grid">
                    <div className="macro-card calories">
                      <span className="macro-label">Calorias</span>
                      <span className="macro-value">{formatNumber(ingredientDetail.calories_per_100g)}</span>
                      <span className="macro-unit">kcal/100g</span>
                    </div>
                    <div className="macro-card protein">
                      <span className="macro-label">Proteína</span>
                      <span className="macro-value">{formatNumber(ingredientDetail.protein_per_100g)}</span>
                      <span className="macro-unit">g/100g</span>
                    </div>
                    <div className="macro-card carbs">
                      <span className="macro-label">Carboidratos</span>
                      <span className="macro-value">{formatNumber(ingredientDetail.carbs_per_100g)}</span>
                      <span className="macro-unit">g/100g</span>
                    </div>
                    <div className="macro-card fat">
                      <span className="macro-label">Gordura</span>
                      <span className="macro-value">{formatNumber(ingredientDetail.fat_per_100g)}</span>
                      <span className="macro-unit">g/100g</span>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h4>Informações Nutricionais Completas</h4>
                    <div className="nutrients-grid">
                      <div className="nutrient-item">
                        <span className="label">Fibra</span>
                        <span className="value">{formatNumber(ingredientDetail.fiber_per_100g)}g</span>
                      </div>
                      <div className="nutrient-item">
                        <span className="label">Sódio</span>
                        <span className="value">{formatNumber(ingredientDetail.sodium_per_100g)}mg</span>
                      </div>
                      <div className="nutrient-item">
                        <span className="label">Açúcar</span>
                        <span className="value">{formatNumber(ingredientDetail.sugar_per_100g)}g</span>
                      </div>
                      <div className="nutrient-item">
                        <span className="label">Cálcio</span>
                        <span className="value">{formatNumber(ingredientDetail.calcium_per_100g)}mg</span>
                      </div>
                      <div className="nutrient-item">
                        <span className="label">Ferro</span>
                        <span className="value">{formatNumber(ingredientDetail.iron_per_100g)}mg</span>
                      </div>
                      <div className="nutrient-item">
                        <span className="label">Vitamina C</span>
                        <span className="value">{formatNumber(ingredientDetail.vitamin_c_per_100g)}mg</span>
                      </div>
                      <div className="nutrient-item">
                        <span className="label">Vitamina A</span>
                        <span className="value">{formatNumber(ingredientDetail.vitamin_a_per_100g)}µg</span>
                      </div>
                      <div className="nutrient-item">
                        <span className="label">Índice Glicêmico</span>
                        <span className="value">{ingredientDetail.glycemic_index || '-'}</span>
                      </div>
                    </div>
                  </div>

                  {ingredientDetail.allergens && (
                    <div className="detail-section">
                      <h4>Alérgenos</h4>
                      <p className="allergens-text">{ingredientDetail.allergens}</p>
                    </div>
                  )}

                  <div className="detail-footer">
                    <span className={`organic-badge ${ingredientDetail.is_organic ? 'organic' : ''}`}>
                      {ingredientDetail.is_organic ? '🌿 Orgânico' : 'Convencional'}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="modal-error">
                <Warning sx={{ fontSize: 48 }} />
                <p>Erro ao carregar detalhes</p>
              </div>
            )}
      </FormModal>
    </div>
  );
};

// ==================== CATEGORY MODAL COMPONENT ====================
const CategoryModal = ({ mode, data, loading, onClose, onSave }) => {
  const [form, setForm] = useState({
    category_name: data?.category_name || '',
    category_description: data?.category_description || '',
    color_hex: data?.color_hex || '#9B59B6',
    icon_name: data?.icon_name || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const cleanData = {
      category_name: form.category_name.trim()
    };
    if (form.category_description?.trim()) {
      cleanData.category_description = form.category_description.trim();
    }
    if (form.color_hex) {
      cleanData.color_hex = form.color_hex;
    }
    if (form.icon_name?.trim()) {
      cleanData.icon_name = form.icon_name.trim();
    }
    onSave(cleanData);
  };

  return (
    <FormModal
      open
      onClose={onClose}
      title={mode === 'create' ? 'Nova Categoria' : 'Editar Categoria'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" form="category-form" loading={loading}>
            Salvar
          </Button>
        </>
      }
    >
        <form id="category-form" onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>Nome da Categoria *</label>
              <input
                type="text"
                className="admin-input"
                value={form.category_name}
                onChange={(e) => setForm({ ...form, category_name: e.target.value })}
                placeholder="Ex: Proteínas, Carboidratos..."
                required
              />
            </div>
            
            <div className="form-group">
              <label>Descrição</label>
              <textarea
                className="admin-input"
                rows={3}
                value={form.category_description}
                onChange={(e) => setForm({ ...form, category_description: e.target.value })}
                placeholder="Descrição da categoria..."
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  <Palette sx={{ fontSize: 16, marginRight: '4px', verticalAlign: 'middle' }} />
                  Cor
                </label>
                <div className="color-picker-wrapper">
                  <input
                    type="color"
                    className="color-picker"
                    value={form.color_hex}
                    onChange={(e) => setForm({ ...form, color_hex: e.target.value })}
                  />
                  <input
                    type="text"
                    className="admin-input color-input"
                    value={form.color_hex}
                    onChange={(e) => setForm({ ...form, color_hex: e.target.value })}
                    placeholder="#RRGGBB"
                    pattern="^#[0-9A-Fa-f]{6}$"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Ícone (Material Design)</label>
                <input
                  type="text"
                  className="admin-input"
                  value={form.icon_name}
                  onChange={(e) => setForm({ ...form, icon_name: e.target.value })}
                  placeholder="Ex: local_cafe, grain..."
                />
              </div>
            </div>
          </div>
        </form>
    </FormModal>
  );
};

// ==================== INGREDIENT MODAL COMPONENT ====================
const IngredientModal = ({ mode, data, categories, loading, onClose, onSave }) => {
  const [form, setForm] = useState({
    name: data?.name || '',
    category: data?.category || '',
    calories_per_100g: data?.calories_per_100g || '',
    protein_per_100g: data?.protein_per_100g || '',
    carbs_per_100g: data?.carbs_per_100g || '',
    fat_per_100g: data?.fat_per_100g || '',
    fiber_per_100g: data?.fiber_per_100g || '',
    sodium_per_100g: data?.sodium_per_100g || '',
    sugar_per_100g: data?.sugar_per_100g || '',
    calcium_per_100g: data?.calcium_per_100g || '',
    iron_per_100g: data?.iron_per_100g || '',
    vitamin_c_per_100g: data?.vitamin_c_per_100g || '',
    vitamin_a_per_100g: data?.vitamin_a_per_100g || '',
    glycemic_index: data?.glycemic_index || '',
    is_organic: data?.is_organic || false,
    allergens: Array.isArray(data?.allergens) ? data.allergens.join(', ') : (data?.allergens || '')
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const cleanData = {
      name: form.name.trim(),
      category: form.category
    };

    // Adicionar campos numéricos se tiver valor
    const numericFields = [
      'calories_per_100g', 'protein_per_100g', 'carbs_per_100g', 
      'fat_per_100g', 'fiber_per_100g', 'sodium_per_100g', 
      'sugar_per_100g', 'calcium_per_100g', 'iron_per_100g',
      'vitamin_c_per_100g', 'vitamin_a_per_100g', 'glycemic_index'
    ];

    numericFields.forEach(field => {
      if (form[field] !== '' && form[field] !== null) {
        cleanData[field] = parseFloat(form[field]) || 0;
      }
    });

    cleanData.is_organic = form.is_organic;

    if (form.allergens?.trim()) {
      cleanData.allergens = form.allergens.split(',').map(a => a.trim()).filter(Boolean);
    }

    onSave(cleanData);
  };

  return (
    <FormModal
      open
      onClose={onClose}
      title={mode === 'create' ? 'Novo Ingrediente' : 'Editar Ingrediente'}
      large
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" form="ingredient-form" loading={loading}>
            Salvar
          </Button>
        </>
      }
    >
        <form id="ingredient-form" onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group flex-2">
                <label>Nome do Ingrediente *</label>
                <input
                  type="text"
                  className="admin-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex: Arroz Branco, Frango Grelhado..."
                  required
                />
              </div>
              <div className="form-group flex-1">
                <label>Categoria *</label>
                <select
                  className="admin-input"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  required
                >
                  <option value="">Selecione...</option>
                  {categories.map(cat => (
                    <option key={cat.idingredientcategory || cat.category_name} value={cat.category_name}>
                      {cat.category_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-section">
              <h4>Macronutrientes (por 100g)</h4>
              <div className="macros-form-grid">
                <div className="form-group">
                  <label>Calorias (kcal)</label>
                  <input
                    type="number"
                    className="admin-input"
                    value={form.calories_per_100g}
                    onChange={(e) => setForm({ ...form, calories_per_100g: e.target.value })}
                    min="0"
                    step="0.1"
                  />
                </div>
                <div className="form-group">
                  <label>Proteína (g)</label>
                  <input
                    type="number"
                    className="admin-input"
                    value={form.protein_per_100g}
                    onChange={(e) => setForm({ ...form, protein_per_100g: e.target.value })}
                    min="0"
                    step="0.1"
                  />
                </div>
                <div className="form-group">
                  <label>Carboidratos (g)</label>
                  <input
                    type="number"
                    className="admin-input"
                    value={form.carbs_per_100g}
                    onChange={(e) => setForm({ ...form, carbs_per_100g: e.target.value })}
                    min="0"
                    step="0.1"
                  />
                </div>
                <div className="form-group">
                  <label>Gordura (g)</label>
                  <input
                    type="number"
                    className="admin-input"
                    value={form.fat_per_100g}
                    onChange={(e) => setForm({ ...form, fat_per_100g: e.target.value })}
                    min="0"
                    step="0.1"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h4>Outros Nutrientes</h4>
              <div className="nutrients-form-grid">
                <div className="form-group">
                  <label>Fibra (g)</label>
                  <input
                    type="number"
                    className="admin-input"
                    value={form.fiber_per_100g}
                    onChange={(e) => setForm({ ...form, fiber_per_100g: e.target.value })}
                    min="0"
                    step="0.1"
                  />
                </div>
                <div className="form-group">
                  <label>Sódio (mg)</label>
                  <input
                    type="number"
                    className="admin-input"
                    value={form.sodium_per_100g}
                    onChange={(e) => setForm({ ...form, sodium_per_100g: e.target.value })}
                    min="0"
                    step="0.1"
                  />
                </div>
                <div className="form-group">
                  <label>Açúcar (g)</label>
                  <input
                    type="number"
                    className="admin-input"
                    value={form.sugar_per_100g}
                    onChange={(e) => setForm({ ...form, sugar_per_100g: e.target.value })}
                    min="0"
                    step="0.1"
                  />
                </div>
                <div className="form-group">
                  <label>Índice Glicêmico</label>
                  <input
                    type="number"
                    className="admin-input"
                    value={form.glycemic_index}
                    onChange={(e) => setForm({ ...form, glycemic_index: e.target.value })}
                    min="0"
                    max="100"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h4>Micronutrientes (por 100g)</h4>
              <div className="micronutrients-form-grid">
                <div className="form-group">
                  <label>Cálcio (mg)</label>
                  <input
                    type="number"
                    className="admin-input"
                    value={form.calcium_per_100g}
                    onChange={(e) => setForm({ ...form, calcium_per_100g: e.target.value })}
                    min="0"
                    step="0.1"
                  />
                </div>
                <div className="form-group">
                  <label>Ferro (mg)</label>
                  <input
                    type="number"
                    className="admin-input"
                    value={form.iron_per_100g}
                    onChange={(e) => setForm({ ...form, iron_per_100g: e.target.value })}
                    min="0"
                    step="0.1"
                  />
                </div>
                <div className="form-group">
                  <label>Vitamina C (mg)</label>
                  <input
                    type="number"
                    className="admin-input"
                    value={form.vitamin_c_per_100g}
                    onChange={(e) => setForm({ ...form, vitamin_c_per_100g: e.target.value })}
                    min="0"
                    step="0.1"
                  />
                </div>
                <div className="form-group">
                  <label>Vitamina A (mcg)</label>
                  <input
                    type="number"
                    className="admin-input"
                    value={form.vitamin_a_per_100g}
                    onChange={(e) => setForm({ ...form, vitamin_a_per_100g: e.target.value })}
                    min="0"
                    step="0.1"
                  />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group flex-2">
                <label>Alérgenos (separados por vírgula)</label>
                <input
                  type="text"
                  className="admin-input"
                  value={form.allergens}
                  onChange={(e) => setForm({ ...form, allergens: e.target.value })}
                  placeholder="Ex: glúten, lactose, amendoim..."
                />
              </div>
              <div className="form-group flex-1">
                <label className="checkbox-label-inline">
                  <input
                    type="checkbox"
                    checked={form.is_organic}
                    onChange={(e) => setForm({ ...form, is_organic: e.target.checked })}
                  />
                  <span>🌿 Orgânico</span>
                </label>
              </div>
            </div>
          </div>
        </form>
    </FormModal>
  );
};

export default IngredientsPage;
